"use client";

import type { FC, ReactNode } from "react";
import { CustomNoDivTooltip } from "@akashnetwork/ui/components";
import { cn } from "@akashnetwork/ui/utils";
import { Cpu, Gpu, HardDrive, MemoryStick } from "lucide-react";
import type { DeploymentDto } from "@src/types/deployment";
import { roundDecimal } from "@src/utils/mathHelpers";
import { formatByteSize } from "@src/utils/unitUtils";
import { formatGpuLabel, getDeploymentGpuModels, type GpuSummary } from "../DeploymentDetail/DeploymentPlacements/placementModel";

/** Column widths fit the widest value each spec can hold at this font size, so every row's specs land on the same x-positions. */
const LAYOUT_CLASSES = {
  inline: {
    container: "flex flex-wrap items-center gap-x-4 gap-y-1.5",
    spec: "whitespace-nowrap"
  },
  columns: {
    container: "grid grid-cols-[5.5rem_5.75rem] items-center gap-x-3 gap-y-1.5",
    spec: "min-w-0"
  }
} as const;
export type DeploymentSpecLayout = keyof typeof LAYOUT_CLASSES;

/** CustomNoDivTooltip, so the trigger stays the grid item and keeps the truncation the columns layout needs. */
export const DEPENDENCIES = {
  CustomTooltip: CustomNoDivTooltip
};
export interface DeploymentSpecSummaryProps {
  deployment: Pick<DeploymentDto, "cpuAmount" | "gpuAmount" | "memoryAmount" | "storageAmount" | "groups">;
  /** Its own prop rather than part of the deployment, because rows render this from places that hold no leases. */
  resolvedGpus?: GpuSummary[];
  layout?: DeploymentSpecLayout;
  className?: string;
  dependencies?: typeof DEPENDENCIES;
}
export const DeploymentSpecSummary: FC<DeploymentSpecSummaryProps> = ({
  deployment,
  resolvedGpus,
  layout = "inline",
  className,
  dependencies: d = DEPENDENCIES
}) => {
  const hasGpu = !!deployment.gpuAmount;
  const layoutClasses = LAYOUT_CLASSES[layout];
  return <div className={cn("text-xs text-muted-foreground", layoutClasses.container, className)} data-sentry-component="DeploymentSpecSummary" data-sentry-source-file="DeploymentSpecSummary.tsx">
      <Spec label="vCPU" icon={<Cpu className="h-3.5 w-3.5" />} value={roundDecimal(deployment.cpuAmount, 2)} className={layoutClasses.spec} dependencies={d} data-sentry-element="Spec" data-sentry-source-file="DeploymentSpecSummary.tsx" />
      <Spec label="Memory" icon={<MemoryStick className="h-3.5 w-3.5" />} value={formatByteSize(deployment.memoryAmount)} className={layoutClasses.spec} dependencies={d} data-sentry-element="Spec" data-sentry-source-file="DeploymentSpecSummary.tsx" />
      <Spec label="Storage" icon={<HardDrive className="h-3.5 w-3.5" />} value={formatByteSize(deployment.storageAmount)} className={layoutClasses.spec} dependencies={d} data-sentry-element="Spec" data-sentry-source-file="DeploymentSpecSummary.tsx" />
      {hasGpu && <Spec label="GPU" icon={<Gpu className="h-3.5 w-3.5" />} value={formatGpuLabel(deployment.gpuAmount ?? 0, getDeploymentGpuModels(deployment.groups), resolvedGpus)} className={layoutClasses.spec} dependencies={d} />}
    </div>;
};

/** The columns layout truncates the value, and a long GPU model list is otherwise unrecoverable. */
const Spec: FC<{
  label: string;
  icon: ReactNode;
  value: string | number;
  className?: string;
  dependencies: typeof DEPENDENCIES;
}> = ({
  label,
  icon,
  value,
  className,
  dependencies: d
}) => <d.CustomTooltip title={`${label}: ${value}`} className="p-3" data-sentry-element="d.CustomTooltip" data-sentry-component="Spec" data-sentry-source-file="DeploymentSpecSummary.tsx">
    <span className={cn("inline-flex cursor-help items-center gap-1.5", className)}>
      <span className="shrink-0" aria-hidden="true">
        {icon}
      </span>
      <span className="truncate" aria-label={label}>
        {value}
      </span>
    </span>
  </d.CustomTooltip>;